import { HttpBackend, HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AppFile } from '@gqlSchema';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../authentication/services/auth.service';
import { UiService } from './ui.service';

@Injectable({
  providedIn: 'root',
})
export class FileUploadService {
  private _http = inject(HttpClient);
  private _auth = inject(AuthService);
  private _ui = inject(UiService);
  private _tts = inject(TranslateService);
  private _rawHttp = new HttpClient(inject(HttpBackend));

  postFiles(file: FormData, isImage = false) {
    const fileUploadEndpoint = isImage
      ? `${environment.fileUpload}?dataContainer=IMAGES`
      : `${environment.fileUpload}?dataContainer=DOCUMENTS`;

    //! to support multiple file submitting
    // const formData: FormData = new FormData();
    // for (let i = 0; i < filesToUpload.length; i++) {
    //   const fileToUpload: any = filesToUpload.item(i);
    //   formData.append('file_' + 1, fileToUpload, fileToUpload.name);
    // }

    return this._http.post<AppFile[]>(fileUploadEndpoint, file, {
      headers: { Authorization: `Bearer ${this._auth.user().authToken}` },
    });
  }

  /**
   * Downloads a stored file through the API's own `/file/download` endpoint.
   *
   * The storage URLs on AppFile point straight at Azure blob storage, which rejects a
   * request carrying our Bearer token (and the containers are not open to XHR anyway) —
   * they are fine as an `<img src>`, not as a download. The API reads the blob with its
   * own credentials and streams it back under the original file name.
   */
  downloadAppFile(fileId: string, fileName: string) {
    // baseUrl, not fileUpload: the endpoint is authorized, so it has to be the same host
    // that issued the token (environment.ts points fileUpload at the dev server while the
    // API runs on localhost, which would answer 401).
    this.download(`${environment.baseUrl}/file/download?FileId=${fileId}`, fileName);
  }

  downloadFile(fileUrl: string, fileName: string) {
    this.download(fileUrl, fileName);
  }

  private download(requestUrl: string, fileName: string) {
    // Deliberately on HttpBackend (no interceptors): errorInterceptor rethrows
    // `err.error.errors[0]`, which is undefined for a plain REST failure, so a broken
    // download arrived here with no status at all. The auth header is set below anyway.
    this._rawHttp.get(requestUrl, {
      headers: { Authorization: `Bearer ${this._auth.user().authToken}` },
      responseType: 'blob'
    }).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Error downloading file:', requestUrl, error?.status, error);
        // Used to fail silently, which made a broken download look like a dead button.
        // The status code is appended after translating, so it survives the lookup.
        // Status 0 is falsy but meaningful (network / CORS), so check for presence.
        const message = this._tts.instant('Could not download the file');
        const status = error?.status;

        this._ui.errorSnack(status == null ? message : `${message} (${status})`);
      }
    });
  }
}
